import { Component, computed, inject, input, } from '@angular/core';
import { JobDetails } from '../../models/details.model';
import { DetailsUtilService } from '../../services/details-utils.service';
import { TranslocoModule } from '@jsverse/transloco';
import { NgClass } from '@angular/common';
import { SharedService } from '../../../jobs/services/shared.service';
import { HighlightDirective } from '../../../../core/directives/highlighter.dir';

@Component({
  selector: 'app-details-main',
  imports: [TranslocoModule, NgClass, HighlightDirective],
  templateUrl: './details-main.html',
  styleUrl: './details-main.scss'
})
export class DetailsMain {
  protected detailsUtilService = inject(DetailsUtilService)
  protected sharedService = inject(SharedService)
  jobDetails = input<JobDetails | null>(null);
  keyword = input<string>('');

  experience = computed(() =>
    this.sharedService.sanitizeCardProperties(this.detailsUtilService.splitExperience(
      this.jobDetails()?.experience ?? '').experiences
    )
  )

  experienceBusinessArea = ((): string[] | undefined => {
    return this.jobDetails()?.experienceBusinessArea.split(',').map(item => item.trim());
  })

  addExperience = computed(() =>
    this.sharedService.sanitizeCardProperties(this.detailsUtilService.splitExperience(
      this.jobDetails()?.experience ?? '').additionalExperience
    )
  )

  gender = computed(() => (this.detailsUtilService.getGender(this.jobDetails()?.Gender ?? '')))

  readonly skillsArray = computed(() => {
    const skills = this.jobDetails()?.SkillsRequired || '';
    return skills.split(',').map(skill => skill.trim());
  });

  readonly suggestedSkills = computed(
    () => this.jobDetails()?.SuggestedSkills?.split(',').map(skill => skill.trim())
  )


  private extractListItems(html: string): string[] {
    if (!html) return [];
    const matches = html.match(/<(li|p)[^>]*>([\s\S]*?)<\/\1>/gi) ?? [];
    return matches
      .map(tag => tag.replace(/^<(li|p)[^>]*>|<\/(li|p)>$/gi, ''))
      .map(text => text.replace(/<[^>]+>/g, '').trim())
      .filter(text => text.length > 0);
  }

  private readonly otherBenefitsItems = computed(() =>
    this.extractListItems(this.jobDetails()?.JobOtherBenifits ?? '')
  );

  private matchItem(pattern: RegExp): string | undefined {
    return this.otherBenefitsItems().find(item => pattern.test(item));
  }

  private extractValue(pattern: RegExp): string {
    const item = this.matchItem(pattern);
    if (item === undefined || item.length === 0) return '';
    const idx = item.indexOf(':');
    return idx === -1 ? '' : item.slice(idx + 1).trim();
  }

  readonly lunchFacility = computed(() =>
    this.extractValue(/^lunch\s*facilit(y|ies)\s*:/i)
  );

  readonly salaryReview = computed(() =>
    this.extractValue(/^salary\s*review\s*:/i)
  );

  readonly festivalBonus = computed(() =>
    this.extractValue(/^festival\s*bonus\s*:/i)
  );

  private splitCommaItems(items: string[]): string[] {
    return items.flatMap(item =>
      item.includes(':')
        ? [item]
        : item.split(',')
          .map(s => s.trim().replace(/\.$/, ''))
          .filter(Boolean)
    );
  }
  readonly addExperienceParsed = computed((): { label: string; chips: string[]; plainItems: string[] } => {
    const raw = this.detailsUtilService.splitExperience(
      this.jobDetails()?.experience ?? ''
    ).additionalExperience ?? '';

    if (!raw) return { label: '', chips: [], plainItems: [] };

    // Extract each <li> separately so boundaries aren't lost
    const liMatches = raw.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) ?? [];
    const items = liMatches
      .map(li => li.replace(/^<li[^>]*>|<\/li>$/gi, '').replace(/<[^>]+>/g, '').trim())
      .filter(Boolean);

    let label = '';
    const chips: string[] = [];
    const plainItems: string[] = [];

    for (const item of items) {
      const idx = item.indexOf(':');
      if (idx !== -1) {
        // has a label — split off the chips
        if (!label) label = item.slice(0, idx + 1).trim();
        const rest = item.slice(idx + 1);
        chips.push(...rest.split(',').map(s => s.trim().replace(/\.$/, '')).filter(Boolean));
      } else if (item.includes(',')) {
        chips.push(...item.split(',').map(s => s.trim().replace(/\.$/, '')).filter(Boolean));
      } else {
        // standalone sentence, e.g. "Freshers are also encouraged to apply."
        plainItems.push(item);
      }
    }

    return { label, chips, plainItems };
  });
  readonly compensationAndBenefits = computed(() => {
    const lunch = this.matchItem(/^lunch\s*facilit(y|ies)\s*:/i);
    const salary = this.matchItem(/^salary\s*review\s*:/i);
    const festival = this.matchItem(/^festival\s*bonus\s*:/i);

    const filtered = this.otherBenefitsItems().filter(
      item => item !== lunch && item !== salary && item !== festival
    );

    return this.splitCommaItems(filtered);
  });

  readonly hasCompensationSection = computed(() => {
    return (this.jobDetails()?.JobOtherBenifits?.trim().length ?? 0) > 0;
  });

  // ---- Responsibilities / Requirements ----

  readonly responsibilities = computed(() => {
    const raw = this.jobDetails()?.JobDescription ?? '';

    const html = raw
      .replace(/<a /g, '<a class="text-[#0E73A9]" ')
      .replace(/<ol[^>]*>/gi, '<ul class="list-disc px-6">')
      .replace(/<\/ol>/gi, '</ul>');

    return this.sharedService.sanitizeCardProperties(html);
  });

  hasAdditionalExperience = computed(() => {
    return (
      (this.jobDetails()?.AdditionJobRequirements?.trim().length ?? 0) > 0
    );
  });

  showRequirements = computed(() => {
    const details = this.jobDetails();

    return (
      (details?.EducationRequirements?.trim().length ?? 0) > 0 ||
      (details?.experience?.trim().length ?? 0) > 0 ||
      this.hasAdditionalExperience() ||
      (details?.AdditionJobRequirements?.trim().length ?? 0) > 0 ||
      (
        (details?.Age?.trim().length ?? 0) > 0 &&
        details?.Age !== 'Na'
      )
    );
  });
}